/**
 * AdController (PRD §14): IMA loader, AdDisplayContainer, AdsLoader/AdsManager.
 * IMA is always loaded from Google's URL (C3) and never modified; its ad UI is never styled.
 */
import { IMA_SDK_URL } from './config.js';

/**
 * Load ima3.js once from imasdk.googleapis.com, with a 5 s ready timeout (§3.1 ERR-1).
 * @param {object} options
 * @param {Window} options.win
 * @param {Function} options.loadScript (url) => Promise
 * @param {number} options.timeoutMs
 * @param {Function} options.setTimeoutFn
 * @returns {Promise<object>} google.ima
 */
export function loadIma({ win, loadScript, timeoutMs, setTimeoutFn }) {
  if (win.google && win.google.ima) return Promise.resolve(win.google.ima);
  if (!loadIma.pending) {
    loadIma.pending = new Promise((resolve, reject) => {
      setTimeoutFn(() => reject(new Error('ima_load')), timeoutMs);
      loadScript(IMA_SDK_URL, {}).then(
        () => (win.google && win.google.ima ? resolve(win.google.ima) : reject(new Error('ima_load'))),
        () => reject(new Error('ima_load')),
      );
    });
    loadIma.pending.catch(() => {
      loadIma.pending = null;
    });
  }
  return loadIma.pending;
}
loadIma.pending = null;

/**
 * Create the ad controller for one player.
 * @param {object} deps
 * @param {object} deps.ima google.ima namespace
 * @param {HTMLVideoElement} deps.video content element, reused by IMA on iOS (§9)
 * @param {Element} deps.adContainer IMA ad container div
 * @param {Function} deps.setTimeoutFn
 * @param {Function} deps.clearTimeoutFn
 * @param {Function} deps.onAdEvent (type, detail) => void
 * @returns {object}
 */
export function createAdController({ ima, video, adContainer, setTimeoutFn, clearTimeoutFn, onAdEvent }) {
  // C22 / §12: no VPAID that runs code.
  try {
    ima.settings.setVpaidMode(ima.ImaSdkSettings.VpaidMode.DISABLED);
    ima.settings.setLocale('en');
  } catch (error) {
    // Older SDK builds: defaults are fine.
  }
  const displayContainer = new ima.AdDisplayContainer(adContainer, video);
  const loader = new ima.AdsLoader(displayContainer);
  const state = { manager: null, started: false, request: null, timer: null, initialized: false, maxAdSeconds: 30, adStartAt: 0 };

  /**
   * Safe wrapper for every IMA callback (§14: any unexpected error collapses safely).
   * @param {Function} fn
   * @returns {Function}
   */
  const guard = (fn) => (event) => {
    try {
      fn(event);
    } catch (error) {
      onAdEvent('script_error', { error });
    }
  };

  /** Destroy the current ads manager (ERR-4/5, close, teardown). */
  function destroyManager() {
    clearTimeoutFn(state.timer);
    if (state.manager) {
      try {
        state.manager.destroy();
      } catch (error) {
        // Already destroyed.
      }
    }
    state.manager = null;
    state.request = null;
    state.preloaded = false;
  }

  loader.addEventListener(
    ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
    guard((event) => {
      const request = state.request;
      if (!request || request.done) return; // late response after our own timeout: ignore
      clearTimeoutFn(state.timer);
      request.done = true;
      const settings = new ima.AdsRenderingSettings();
      settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
      // 2.0.6 (A49): a creative whose media has not started in time is dropped as error-before-start. Back to IMA's
      // 8 s default: at 5 s, VAST 402 (media timeout) was ~60% of GAM video errors (24 Oct 2026).
      settings.loadVideoTimeout = 8000;
      // Prefer a small MP4 rendition so the media starts faster (fewer 402s), and never pick HLS or WebM.
      // 2.0.13: per device (video.bitrateKbps / phoneBitrateKbps), 600 kbps on phones; was 1000 everywhere.
      settings.bitrate = request.bitrate || 1000; // kbps
      // 2.0.13: preload the media after init(), so a held ad can buffer before it starts (see preload()).
      settings.enablePreloading = true;
      settings.mimeTypes = ['video/mp4'];
      const manager = event.getAdsManager(video, settings);
      state.manager = manager;
      bindManager(manager);
      onAdEvent('loaded', {});
    }),
    false,
  );
  loader.addEventListener(
    ima.AdErrorEvent.Type.AD_ERROR,
    guard((event) => {
      const request = state.request;
      if (!request || request.done) return;
      clearTimeoutFn(state.timer);
      request.done = true;
      const error = event.getError && event.getError();
      onAdEvent('empty', { code: error && error.getErrorCode ? error.getErrorCode() : 'ad_error' });
    }),
    false,
  );

  /**
   * Attach the manager listeners.
   * @param {object} manager
   */
  function bindManager(manager) {
    const Type = ima.AdEvent.Type;
    // End the break once, on whichever comes first: complete, skipped, all ads completed or content resume.
    // IMA does not always send CONTENT_RESUME_REQUESTED after a skip (e.g. a stopped pod), which left the
    // player black in the ad state.
    const endBreak = guard(() => {
      if (state.manager !== manager) return; // already ended, or a newer break
      const seconds = state.adStartAt ? (Date.now() - state.adStartAt) / 1000 : 0;
      destroyManager();
      state.started = false;
      state.adStartAt = 0;
      onAdEvent('break-ended', { adSeconds: seconds });
    });
    manager.addEventListener(
      ima.AdErrorEvent.Type.AD_ERROR,
      guard((event) => {
        const error = event.getError && event.getError();
        const code = error && error.getErrorCode ? error.getErrorCode() : 'ad_error';
        const wasStarted = state.started;
        destroyManager();
        onAdEvent(wasStarted ? 'error-after-start' : 'error-before-start', { code }); // ERR-4 / ERR-5
      }),
    );
    manager.addEventListener(
      Type.LOADED,
      guard((event) => {
        const ad = event.getAd && event.getAd();
        if (!ad) return;
        // C22: linear only. C23: a break never runs over 31 s unless skippable within 5 s.
        const tooLong = ad.getDuration() > state.maxAdSeconds + 1 && !(ad.isSkippable && ad.isSkippable());
        if (!ad.isLinear() || tooLong) {
          destroyManager();
          onAdEvent('error-before-start', { code: ad.isLinear() ? 'ad_too_long' : 'non_linear' });
        }
      }),
    );
    manager.addEventListener(
      Type.STARTED,
      guard((event) => {
        const ad = event.getAd && event.getAd();
        const pod = ad && ad.getAdPodInfo && ad.getAdPodInfo();
        // C23: one ad per break. A second ad in a pod is stopped before it plays on.
        if (state.started && pod && pod.getAdPosition() > 1) {
          manager.stop();
          endBreak();
          return;
        }
        state.started = true;
        state.adStartAt = Date.now();
        onAdEvent('started', {
          duration: ad ? ad.getDuration() : 0,
          skippable: !!(ad && ad.isSkippable && ad.isSkippable()),
        });
      }),
    );
    // [IMA event, our event, detail, ends the break]. 2.0.5 (A49): AD_BUFFERING shows the spinner; AD_PROGRESS
    // (and every other event) clears it.
    [
      [Type.FIRST_QUARTILE, 'progress', { percent: 25 }],
      [Type.MIDPOINT, 'progress', { percent: 50 }],
      [Type.THIRD_QUARTILE, 'progress', { percent: 75 }],
      [Type.COMPLETE, 'complete', {}, 1],
      [Type.SKIPPED, 'skipped', {}, 1],
      [Type.AD_BUFFERING, 'buffering', {}],
      [Type.AD_PROGRESS, 'playing', {}],
      [Type.CLICK, 'click', {}], // C12
    ].forEach(([type, name, detail, ends]) =>
      manager.addEventListener(
        type,
        guard(() => {
          onAdEvent(name, detail);
          if (ends) endBreak();
        }),
      ),
    );
    manager.addEventListener(Type.ALL_ADS_COMPLETED, endBreak);
    manager.addEventListener(Type.CONTENT_RESUME_REQUESTED, endBreak);
  }

  return {
    state,
    /**
     * Request one pre-roll (C20: one request per break, no tag chaining).
     * @param {object} input
     * @param {string} input.url VAST URL
     * @param {number} input.width real player width (§7.2 linearAdSlotWidth)
     * @param {number} input.height real player height
     * @param {boolean} input.muted
     * @param {boolean} input.autoplay
     * @param {boolean} input.continuousPlayback auto-advance on
     * @param {number} input.timeoutMs 4000, or 5000 with Prebid (§3)
     * @param {number} input.maxAdSeconds max_ad_duration in seconds
     * @param {number} [input.bitrate] preferred media bitrate in kbps (2.0.13)
     */
    request({ url, width, height, muted, autoplay, continuousPlayback, timeoutMs, maxAdSeconds, bitrate }) {
      destroyManager();
      state.started = false;
      state.maxAdSeconds = maxAdSeconds;
      const adsRequest = new ima.AdsRequest();
      adsRequest.adTagUrl = url;
      adsRequest.linearAdSlotWidth = width;
      adsRequest.linearAdSlotHeight = height;
      adsRequest.nonLinearAdSlotWidth = width;
      adsRequest.nonLinearAdSlotHeight = 0; // C22: no overlay ads
      adsRequest.vastLoadTimeout = timeoutMs;
      if (adsRequest.setAdWillAutoPlay) adsRequest.setAdWillAutoPlay(autoplay);
      if (adsRequest.setAdWillPlayMuted) adsRequest.setAdWillPlayMuted(muted);
      if (adsRequest.setContinuousPlayback) adsRequest.setContinuousPlayback(continuousPlayback);
      const request = { done: false, bitrate };
      state.request = request;
      // ERR-3: our own VAST timer, so the display fallback starts while the reader is at the slot.
      state.timer = setTimeoutFn(() => {
        if (request.done) return;
        request.done = true;
        onAdEvent('empty', { code: 'timeout' });
      }, timeoutMs);
      if (!state.initialized) {
        displayContainer.initialize();
        state.initialized = true;
      }
      loader.requestAds(adsRequest);
    },
    /**
     * Start the loaded ad (held ads start only when the slot is back in view, §7).
     * @param {number} width
     * @param {number} height
     * @param {boolean} muted
     */
    start(width, height, muted) {
      const manager = state.manager;
      if (!manager) return false;
      if (state.preloaded) manager.resize(width, height, ima.ViewMode.NORMAL);
      else manager.init(width, height, ima.ViewMode.NORMAL);
      manager.setVolume(muted ? 0 : 1);
      manager.start();
      return true;
    },
    /**
     * 2.0.13: init() a held ad without starting it, so IMA starts buffering its media (enablePreloading) while the
     * slot comes into view; start() then plays from the buffer. Nothing plays and no impression fires until start().
     * @param {number} width
     * @param {number} height
     * @returns {boolean} preloading started now
     */
    preload(width, height) {
      const manager = state.manager;
      if (!manager || state.preloaded || state.started) return false;
      manager.init(width, height, ima.ViewMode.NORMAL);
      state.preloaded = true;
      return true;
    },
    pause() {
      if (state.manager) state.manager.pause();
    },
    resume() {
      if (state.manager) state.manager.resume();
    },
    /** @param {boolean} muted UX-5: setVolume(1) on unmute */
    setMuted(muted) {
      if (state.manager) state.manager.setVolume(muted ? 0 : 1);
    },
    /**
     * Resize the live ad in place (§7.2: never re-requests).
     * @param {number} width
     * @param {number} height
     * @param {boolean} fullscreen
     */
    resize(width, height, fullscreen) {
      if (state.manager) state.manager.resize(width, height, fullscreen ? ima.ViewMode.FULLSCREEN : ima.ViewMode.NORMAL);
    },
    hasLoadedAd: () => !!state.manager && !state.started,
    /** @returns {number} seconds of the current ad played so far (0 when none) */
    adElapsed: () => (state.started && state.adStartAt ? (Date.now() - state.adStartAt) / 1000 : 0),
    isAdPlaying: () => !!state.manager && state.started,
    /** Drop the current ad and any pending request. */
    cancel() {
      if (state.request) state.request.done = true;
      destroyManager();
      state.started = false;
    },
    /** §3.2 step 1 order: adsManager.destroy(), then adDisplayContainer.destroy(). */
    destroy() {
      if (state.request) state.request.done = true;
      destroyManager();
      try {
        loader.destroy();
      } catch (error) {
        // Ignore.
      }
      try {
        displayContainer.destroy();
      } catch (error) {
        // Ignore.
      }
    },
  };
}
